import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
const PUBLIC_PATHS = ["/login", "/join", "/forgot-password", "/reset-password", "/verify-email"];
import api from "@/lib/api";
import { FacebookLogo, XLogo, InstagramLogo, LinkedinLogo, YoutubeLogo, TiktokLogo, WhatsappLogo, MapPin, Phone, EnvelopeSimple, Clock, ArrowUpRight } from "@phosphor-icons/react";

export const SOCIALS = [
  { key: "facebook", label: "Facebook", icon: FacebookLogo },
  { key: "x", label: "X (Twitter)", icon: XLogo },
  { key: "instagram", label: "Instagram", icon: InstagramLogo },
  { key: "linkedin", label: "LinkedIn", icon: LinkedinLogo },
  { key: "youtube", label: "YouTube", icon: YoutubeLogo },
  { key: "tiktok", label: "TikTok", icon: TiktokLogo },
  { key: "whatsapp", label: "WhatsApp", icon: WhatsappLogo },
];
export const FOOTER_UPDATED = "site-footer-updated";

const Heading = ({ children }) => (
  <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-white">
    {children}
    <span className="mt-2.5 block h-0.5 w-8 rounded bg-[hsl(var(--primary))]" />
  </h3>
);

function LinkList({ title, links, testId }) {
  if (!links?.length) return null;
  return (
    <div data-testid={testId}>
      <Heading>{title}</Heading>
      <ul className="mt-4 space-y-2.5">
        {links.map((l, i) => (
          <li key={`${l.url}-${i}`}>
            <a href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`${testId}-link-${i}`}
              className="group inline-flex items-center gap-1 text-sm text-white/60 transition-colors duration-200 hover:text-white">
              {l.name}<ArrowUpRight size={12} weight="bold" className="text-[hsl(var(--success))] opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Contact({ f }) {
  const rows = [
    f.address && { icon: MapPin, text: f.address, id: "address" },
    f.phone && { icon: Phone, text: f.phone, href: `tel:${f.phone.replace(/[^\d+]/g, "")}`, id: "phone" },
    f.email && { icon: EnvelopeSimple, text: f.email, href: `mailto:${f.email}`, id: "email" },
    f.office_hours && { icon: Clock, text: f.office_hours, id: "hours" },
  ].filter(Boolean);
  if (!rows.length) return null;
  return (
    <div data-testid="footer-contact">
      <Heading>Reach Us</Heading>
      <ul className="mt-4 space-y-3">
        {rows.map((r) => (
          <li key={r.id} className="flex items-start gap-3 text-sm text-white/60" data-testid={`footer-${r.id}`}>
            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/5 ring-1 ring-white/10">
              <r.icon size={14} weight="bold" className="text-[hsl(var(--primary))] brightness-125" />
            </span>
            {r.href ? <a href={r.href} className="break-all transition-colors duration-200 hover:text-white">{r.text}</a> : <span className="whitespace-pre-line">{r.text}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PublicSiteFooter() {
  const { pathname } = useLocation();
  return PUBLIC_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`)) ? <SiteFooter /> : null;
}

export function SiteFooter() {
  const [f, setF] = useState(null);
  useEffect(() => {
    const load = () => api.get("/site-footer").then((r) => setF(r.data)).catch(() => setF({}));
    load();
    window.addEventListener(FOOTER_UPDATED, load);
    return () => window.removeEventListener(FOOTER_UPDATED, load);
  }, []);
  if (!f) return null;
  const socials = SOCIALS.filter((s) => f.socials?.[s.key]);
  const year = new Date().getFullYear();
  return (
    <footer className="relative overflow-hidden bg-[hsl(var(--header-bg))] text-white print:hidden" data-testid="site-footer">
      <div className="h-1 brand-rule" />
      <div aria-hidden className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[hsl(var(--primary))] opacity-[0.12] blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -bottom-28 left-1/4 h-64 w-64 rounded-full bg-[hsl(var(--success))] opacity-[0.10] blur-3xl" />
      <div className="relative px-6 md:px-10 py-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
        <div>
          <Link to="/" data-testid="footer-logo-home-link" aria-label="MELPWU home" className="flex w-fit items-center gap-3 transition-opacity duration-200 hover:opacity-90">
            <img src="/org_logo.png" alt="MELPWU logo" className="h-12 w-12 rounded-full bg-white p-0.5 object-contain ring-2 ring-[hsl(var(--primary))] ring-offset-2 ring-offset-[hsl(var(--header-bg))]" />
            <div className="leading-tight">
              <p className="font-display text-xl font-bold">MELPWU</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/50">Members Portal</p>
            </div>
          </Link>
          {f.about && <p className="mt-5 max-w-sm text-sm leading-relaxed text-white/60 whitespace-pre-line" data-testid="footer-about">{f.about}</p>}
          {socials.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2" data-testid="footer-socials">
              {socials.map((s) => (
                <a key={s.key} href={f.socials[s.key]} target="_blank" rel="noopener noreferrer" aria-label={s.label} title={s.label}
                  data-testid={`footer-social-${s.key}`}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/5 ring-1 ring-white/15 text-white/80 transition-[background-color,color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[hsl(var(--primary))] hover:text-white">
                  <s.icon size={18} weight="fill" />
                </a>
              ))}
            </div>
          )}
        </div>
        <LinkList title="Our Partners" links={f.partners} testId="footer-partners" />
        <LinkList title="Important Links" links={f.important_links} testId="footer-important" />
        <Contact f={f} />
      </div>
      <div className="relative border-t border-white/10">
        <p className="px-6 md:px-10 py-5 text-xs tracking-wide text-white/45" data-testid="footer-copyright">
          {f.copyright_text || `© ${year} MELPWU. All rights reserved.`}
        </p>
      </div>
    </footer>
  );
}
